import { useEffect, useMemo, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Heart, Minus, Plus, Star, Truck, ShieldCheck, ChevronLeft, ChevronRight, AlertCircle } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import api from "@/lib/api";
import { toast } from "sonner";
import { useCart } from "@/context/CartContext";
import { useWishlist } from "@/context/WishlistContext";
import { useAuth } from "@/context/AuthContext";
import { useCurrency } from "@/context/CurrencyContext";
import { ProductCard } from "@/components/ProductCard";
import { FadeIn } from "@/components/FadeIn";

const SIZE_DIMENSIONS = {
  "LIRA Mini": { width: "60 cm", length: "165 cm" },
  "LIRA Standard": { width: "70 cm", length: "180 cm" },
};

const DEFAULT_DETAILS = [
  "100% Modal",
  "Semi-transparent",
  "Lightweight & breathable",
  "Soft, fluid drape",
  "Ethically produced",
  "Hand wash only",
  "Dry clean recommended as needed",
];

// Strip dangerous markup but keep inline formatting (color/font/bold/line breaks).
const sanitizeDescription = (html) => {
  if (!html) return "";
  return String(html)
    .replace(/<\s*(script|iframe|object|embed|link|meta|style)[^>]*>[\s\S]*?<\s*\/\s*\1\s*>/gi, "")
    .replace(/<\s*(script|iframe|object|embed|link|meta|style)[^>]*\/?>/gi, "")
    .replace(/\son\w+\s*=\s*"[^"]*"/gi, "")
    .replace(/\son\w+\s*=\s*'[^']*'/gi, "")
    .replace(/javascript:/gi, "");
};


const FAQS = [
  { q: "How large is each hijab?", a: "Each Hijab comes in a Mini and Standard size. The Mini measures 165 x 60 cm, while the Standard is 180 x 70 cm." },
  { q: "Will the fabric slip?", a: "Our modal blend has a naturally grippy, matte texture that holds folds securely throughout the day without an underscarf." },
  { q: "How do I care for it?", a: "Hand wash cold or machine wash on a delicate cycle, then lay flat to dry. A cool iron restores a crisp finish." },
  { q: "What is your return policy?", a: "Returns & Exchanges will not be accepted. If you require help with a product you received, please contact us by email: support@lirascarves.com." },
];

const useRecentlyViewed = (slug) => {
  useEffect(() => {
    if (!slug) return;
    try {
      const prev = JSON.parse(localStorage.getItem("lira_recent") || "[]").filter((s) => s !== slug);
      localStorage.setItem("lira_recent", JSON.stringify([slug, ...prev].slice(0, 8)));
    } catch {}
  }, [slug]);
};

export default function ProductDetail() {
  const { slug } = useParams();
  const { addItem } = useCart();
  const { has, toggle } = useWishlist();
  const { format: money } = useCurrency();
  const { user } = useAuth();

  const [product, setProduct] = useState(null);
  const [related, setRelated] = useState([]);
  const [recent, setRecent] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [activeImg, setActiveImg] = useState(0);
  const [color, setColor] = useState(null);
  const [size, setSize] = useState(null);
  const [qty, setQty] = useState(1);
  const [showSticky, setShowSticky] = useState(false);
  const [zoom, setZoom] = useState({ active: false, x: 50, y: 50 });
  const [reviewForm, setReviewForm] = useState({ rating: 5, title: "", body: "" });
  const buyRef = useRef(null);

  useRecentlyViewed(slug);

  useEffect(() => {
    window.scrollTo(0, 0);
    setActiveImg(0); setQty(1);
    api.get(`/products/${slug}`).then((r) => {
      setProduct(r.data);
      setColor(r.data.colors[0]?.name || null);
      const ss = r.data.size_stock || {};
      const avail = (r.data.sizes || []).filter((s) => Object.keys(ss).length === 0 || (ss[s] ?? 0) > 0);
      setSize(avail[0] || r.data.sizes?.[0] || null);
    });
    api.get(`/products/${slug}/related`).then((r) => setRelated(r.data.slice(0, 4)));
    api.get(`/products/${slug}/reviews`).then((r) => setReviews(r.data));
  }, [slug]);

  useEffect(() => {
    const onScroll = () => {
      if (buyRef.current) setShowSticky(buyRef.current.getBoundingClientRect().bottom < 0);
    };
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    let recentSlugs = [];
    try { recentSlugs = JSON.parse(localStorage.getItem("lira_recent") || "[]"); } catch {}
    recentSlugs = recentSlugs.filter((s) => s !== slug).slice(0, 4);
    if (recentSlugs.length) {
      Promise.all(recentSlugs.map((s) => api.get(`/products/${s}`).then((r) => r.data).catch(() => null)))
        .then((list) => setRecent(list.filter(Boolean)));
    } else {
      setRecent([]);
    }
  }, [slug]);

  const avgRating = useMemo(() => {
    if (!reviews.length) return product?.rating || 5;
    return reviews.reduce((s, r) => s + r.rating, 0) / reviews.length;
  }, [reviews, product]);

  if (!product) {
    return <div className="container-lira py-32 text-center text-[#737373]">Loading…</div>;
  }

  const wished = has(product.slug);

  const submitReview = async (e) => {
    e.preventDefault();
    if (!user) { toast.error("Please sign in to leave a review."); return; }
    try {
      const res = await api.post(`/products/${slug}/reviews`, reviewForm);
      setReviews((prev) => [res.data, ...prev]);
      setReviewForm({ rating: 5, title: "", body: "" });
      toast.success("Thank you for your review!");
    } catch {
      toast.error("Could not submit review.");
    }
  };

  const buyNow = () => {
    addItem(product, color, qty, size);
  };

  return (
    <div data-testid="product-detail-page">
      <div className="container-lira py-10 md:py-16">
        <nav className="text-xs text-[#737373] mb-8 tracking-wide">
          <Link to="/" className="hover:text-[#1A1A1A]">Home</Link> / <Link to={`/collection/${product.category}`} className="hover:text-[#1A1A1A]">{product.category_label}</Link> / <span className="text-[#1A1A1A]">{product.name}</span>
        </nav>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16">
          {/* Gallery */}
          <div className="flex flex-col-reverse md:flex-row gap-4">
            <div className="flex md:flex-col gap-3 overflow-x-auto no-scrollbar">
              {product.images.map((img, i) => (
                <button key={i} onClick={() => setActiveImg(i)}
                  className={`w-20 h-24 shrink-0 overflow-hidden rounded-sm bg-beige border ${activeImg === i ? "border-[#1A1A1A]" : "border-transparent"}`}
                  data-testid={`thumbnail-${i}`}>
                  <img src={img} alt={`${product.name} view ${i + 1}`} className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
            <div
              className="flex-1 relative overflow-hidden rounded-sm bg-beige aspect-[3/4] cursor-zoom-in"
              onMouseEnter={() => setZoom((z) => ({ ...z, active: true }))}
              onMouseLeave={() => setZoom((z) => ({ ...z, active: false }))}
              onMouseMove={(e) => {
                const rect = e.currentTarget.getBoundingClientRect();
                setZoom({ active: true, x: ((e.clientX - rect.left) / rect.width) * 100, y: ((e.clientY - rect.top) / rect.height) * 100 });
              }}
              data-testid="product-main-image"
            >
              <img
                src={product.images[activeImg]} alt={product.name}
                className="w-full h-full object-cover transition-transform duration-300"
                style={zoom.active ? { transform: "scale(1.8)", transformOrigin: `${zoom.x}% ${zoom.y}%` } : {}}
              />
            </div>
          </div>

          {/* Info */}
          <div ref={buyRef}>
            <p className="eyebrow mb-3">{product.category_label}</p>
            <h1 className="font-serif text-4xl md:text-5xl font-light tracking-tight">{product.name}</h1>
            <div className="flex items-center gap-3 mt-4">
              <div className="flex items-center gap-1">
                {Array.from({ length: 5 }).map((_, i) => (
                  <Star key={i} className={`h-4 w-4 ${i < Math.round(avgRating) ? "fill-[#1A1A1A] text-[#1A1A1A]" : "text-[#d8d1c4]"}`} />
                ))}
              </div>
              <span className="text-xs text-[#737373]">{product.review_count + reviews.length} reviews</span>
            </div>

            <div className="flex items-center gap-4 mt-6">
              <span className="text-2xl">{money(product.price)}</span>
              {product.compare_at_price && <span className="text-lg text-[#737373] line-through">{money(product.compare_at_price)}</span>}
            </div>

            {product.stock === 0 || product.in_stock === false ? (
              <div className="mt-3 inline-flex items-center gap-2 text-sm text-[#9a3b3b]" data-testid="stock-indicator">
                <span className="h-2 w-2 rounded-full bg-[#9a3b3b]" />
                Out of stock — Restock coming soon
              </div>
            ) : product.stock <= 10 ? (
              <div className="mt-3 inline-flex items-center gap-2 text-sm text-[#b45309]" data-testid="stock-indicator">
                <span className="relative flex h-2.5 w-2.5">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#d98324] opacity-75" />
                  <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-[#b45309]" />
                </span>
                <AlertCircle strokeWidth={1.5} className="h-4 w-4" />
                Low stock — only {product.stock} left
              </div>
            ) : (
              <div className="mt-3 inline-flex items-center gap-2 text-sm text-[#3a6b45]" data-testid="stock-indicator">
                <span className="h-2 w-2 rounded-full bg-[#3a6b45]" />
                In stock — ready to ship
              </div>
            )}

            {product.pre_order && (
              <div className="mt-3 flex items-center gap-2 text-sm text-[#9a3b3b]" data-testid="pre-order-message">
                <span className="h-2 w-2 rounded-full bg-[#9a3b3b]" />
                {product.pre_order_message?.trim() || "Pre-Order items will take 2-3 weeks for delivery"}
              </div>
            )}

            {Array.isArray(product.notices) && product.notices.map((n, i) => (
              n?.text?.trim() ? (
                <div key={i} className="mt-3 flex items-center gap-2 text-sm" style={{ color: n.color || "#560216" }} data-testid={`product-notice-${i}`}>
                  <span className="h-2 w-2 rounded-full" style={{ backgroundColor: n.color || "#560216" }} />
                  {n.text}
                </div>
              ) : null
            ))}

            {product.description && (
              <div
                className="mt-6 text-[#3a3a3a] leading-relaxed lira-rich-desc"
                style={{ whiteSpace: "pre-wrap" }}
                data-testid="product-description-display"
                dangerouslySetInnerHTML={{ __html: sanitizeDescription(product.description) }}
              />
            )}

            {/* Colours */}
            {product.colors.length > 0 && (
              <div className="mt-8">
                <p className="eyebrow mb-3">Colour · {color}</p>
                <div className="flex items-center gap-3">
                  {product.colors.map((c) => (
                    <button key={c.name} onClick={() => setColor(c.name)} title={c.name}
                      className={`h-8 w-8 rounded-full border transition-transform hover:scale-110 ${color === c.name ? "ring-1 ring-offset-2 ring-[#1A1A1A]" : "border-[#E5E5E5]"}`}
                      style={{ backgroundColor: c.hex }} data-testid={`color-${c.name}`} />
                  ))}
                </div>
              </div>
            )}

            {/* Sizes */}
            {product.sizes && product.sizes.length > 0 && (
              <div className="mt-8">
                <div className="flex items-center justify-between mb-3">
                  <p className="eyebrow">Size</p>
                  {product.sizes.some((s) => SIZE_DIMENSIONS[s]) && (
                    <Dialog>
                      <DialogTrigger asChild>
                        <button className="text-xs uppercase tracking-[0.15em] text-[#737373] hover:text-[#1A1A1A] underline underline-offset-4" data-testid="size-chart-button">Size chart</button>
                      </DialogTrigger>
                      <DialogContent className="max-w-md">
                        <DialogHeader>
                          <DialogTitle className="font-serif text-2xl font-light">Size Chart</DialogTitle>
                        </DialogHeader>
                        <div className="mt-4">
                          <table className="w-full text-sm border-collapse">
                            <thead>
                              <tr className="border-b border-[#E5E5E5] text-left">
                                <th className="py-2 font-medium">Size</th>
                                <th className="py-2 font-medium">Width</th>
                                <th className="py-2 font-medium">Length</th>
                              </tr>
                            </thead>
                            <tbody>
                              {product.sizes.filter((s) => SIZE_DIMENSIONS[s]).map((s, i, arr) => (
                                <tr key={s} className={i < arr.length - 1 ? "border-b border-[#F0EEE9]" : ""}>
                                  <td className="py-3">{s}</td>
                                  <td className="py-3">{SIZE_DIMENSIONS[s].width}</td>
                                  <td className="py-3">{SIZE_DIMENSIONS[s].length}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                          <p className="text-xs text-[#737373] mt-4 leading-relaxed">Measurements are approximate. LIRA Mini offers a lighter, more compact drape; LIRA Standard provides fuller coverage and length for versatile styling.</p>
                        </div>
                      </DialogContent>
                    </Dialog>
                  )}
                </div>
                <div className="flex flex-wrap gap-3">
                  {product.sizes.map((s) => {
                    const ss = product.size_stock || {};
                    const tracked = Object.keys(ss).length > 0;
                    const soldOut = tracked && (ss[s] ?? 0) <= 0;
                    return (
                      <button key={s} disabled={soldOut} onClick={() => setSize(s)} data-testid={`size-${s.replace(/\s+/g, "-")}`}
                        className={`px-5 py-2.5 rounded-full text-xs uppercase tracking-[0.15em] border transition-all ${soldOut ? "border-[#E5E5E5] text-[#b0a89a] line-through cursor-not-allowed" : size === s ? "bg-[#1A1A1A] text-white border-[#1A1A1A]" : "border-[#E5E5E5] text-[#1A1A1A] hover:border-[#1A1A1A]"}`}>
                        {s}{soldOut ? " · Sold out" : ""}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Quantity + actions */}
            <div className="mt-8 flex items-center gap-4">
              <div className="flex items-center border border-[#E5E5E5] rounded-full">
                <button className="px-4 py-3" onClick={() => setQty((q) => Math.max(1, q - 1))} aria-label="Decrease"><Minus strokeWidth={1} className="h-4 w-4" /></button>
                <span className="w-8 text-center text-sm" data-testid="quantity-value">{qty}</span>
                <button className="px-4 py-3" onClick={() => setQty((q) => q + 1)} aria-label="Increase"><Plus strokeWidth={1} className="h-4 w-4" /></button>
              </div>
              <button onClick={() => toggle(product)} aria-label="Wishlist" className="h-12 w-12 grid place-items-center rounded-full border border-[#E5E5E5] hover:border-[#1A1A1A] transition-colors" data-testid="pdp-wishlist">
                <Heart strokeWidth={1} className={`h-5 w-5 ${wished ? "fill-[#1A1A1A] text-[#1A1A1A]" : ""}`} />
              </button>
            </div>

            <div className="mt-4 flex flex-col sm:flex-row gap-3">
              {product.stock === 0 || product.in_stock === false ? (
                <button disabled className="btn-secondary flex-1 opacity-50 cursor-not-allowed" data-testid="out-of-stock-button">Out of Stock</button>
              ) : (
                <>
                  <button onClick={() => addItem(product, color, qty, size)} className="btn-secondary flex-1" data-testid="add-to-cart-button">Add to Bag</button>
                  <Link to="/cart" onClick={buyNow} className="btn-primary flex-1" data-testid="buy-now-button">Buy Now</Link>
                </>
              )}
            </div>

            {/* Trust row */}
            <div className="mt-8 grid grid-cols-2 gap-4 border-t border-[#E5E5E5] pt-6 text-center">
              <div className="flex flex-col items-center gap-2"><Truck strokeWidth={1} className="h-5 w-5" /><span className="text-[11px] text-[#737373]">Free shipping $150+ (Canada)</span></div>
              <div className="flex flex-col items-center gap-2"><ShieldCheck strokeWidth={1} className="h-5 w-5" /><span className="text-[11px] text-[#737373]">Secure checkout</span></div>
            </div>

            {/* Details */}
            {(() => {
              const effectiveDetails = product.details_set
                ? (product.details || [])
                : (product.details?.length ? product.details : DEFAULT_DETAILS);
              if (effectiveDetails.length === 0) return null;
              return (
              <div className="mt-8 border-t border-[#E5E5E5] pt-6" data-testid="product-details-list">
                <p className="eyebrow mb-4">Details</p>
                <ul className="space-y-2" style={{ fontFamily: '"Cinzel", serif' }}>
                  {effectiveDetails.map((d, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-[#3a3a3a]">
                      <span className="mt-2 h-1 w-1 rounded-full bg-[#1A1A1A] shrink-0" />
                      {d}
                    </li>
                  ))}
                </ul>
              </div>
              );
            })()}
            <div className="mt-8">
              <Accordion type="single" collapsible className="mt-6">
                <AccordionItem value="shipping"><AccordionTrigger className="eyebrow">Shipping Information</AccordionTrigger><AccordionContent className="text-sm text-[#4a4a4a] leading-relaxed">Pre order items time will vary, additional information will be added soon.</AccordionContent></AccordionItem>
                <AccordionItem value="returns"><AccordionTrigger className="eyebrow">Returns &amp; Exchanges</AccordionTrigger><AccordionContent className="text-sm text-[#4a4a4a] leading-relaxed">Returns &amp; Exchanges will not be accepted. If you require help with a product you received, please contact us by email: support@lirascarves.com.</AccordionContent></AccordionItem>
              </Accordion>
            </div>
          </div>
        </div>
      </div>

      {/* Reviews */}
      <section className="container-lira py-16 md:py-24 border-t border-[#E5E5E5]" data-testid="pdp-reviews">
        <h2 className="font-serif text-3xl md:text-4xl font-light tracking-tight mb-10">Customer Reviews</h2>
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.4fr] gap-12">
          <form onSubmit={submitReview} className="space-y-4 bg-beige p-8 rounded-sm h-fit">
            <p className="eyebrow">Write a review</p>
            <div className="flex items-center gap-1">
              {Array.from({ length: 5 }).map((_, i) => (
                <button key={i} type="button" onClick={() => setReviewForm((f) => ({ ...f, rating: i + 1 }))} aria-label={`${i + 1} star`}>
                  <Star className={`h-6 w-6 ${i < reviewForm.rating ? "fill-[#1A1A1A] text-[#1A1A1A]" : "text-[#d8d1c4]"}`} />
                </button>
              ))}
            </div>
            <input value={reviewForm.title} onChange={(e) => setReviewForm((f) => ({ ...f, title: e.target.value }))} placeholder="Title" required className="w-full bg-white border border-[#E5E5E5] rounded-md px-4 py-3 text-sm outline-none focus:ring-1 focus:ring-black" data-testid="review-title" />
            <textarea value={reviewForm.body} onChange={(e) => setReviewForm((f) => ({ ...f, body: e.target.value }))} placeholder="Share your thoughts…" required rows={4} className="w-full bg-white border border-[#E5E5E5] rounded-md px-4 py-3 text-sm outline-none focus:ring-1 focus:ring-black" data-testid="review-body" />
            <button type="submit" className="btn-primary w-full" data-testid="submit-review">Submit Review</button>
            {!user && <p className="text-xs text-[#737373]">You'll need to <Link to="/account" className="underline">sign in</Link> to post.</p>}
          </form>

          <div className="divide-y divide-[#E5E5E5]">
            {reviews.length === 0 && <p className="text-[#737373]">Be the first to review this piece.</p>}
            {reviews.map((r) => (
              <div key={r.id} className="py-6 flex gap-4">
                {r.avatar ? <img src={r.avatar} alt={r.author} className="h-10 w-10 rounded-full object-cover" /> : <div className="h-10 w-10 rounded-full bg-beige grid place-items-center text-sm">{r.author[0]}</div>}
                <div>
                  <div className="flex items-center gap-1 mb-1">
                    {Array.from({ length: r.rating }).map((_, i) => <Star key={i} className="h-3.5 w-3.5 fill-[#1A1A1A] text-[#1A1A1A]" />)}
                  </div>
                  <p className="font-serif text-lg">{r.title}</p>
                  <p className="text-sm text-[#4a4a4a] mt-1">{r.body}</p>
                  <p className="text-xs text-[#737373] mt-2">{r.author}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="container-lira py-16 md:py-24 border-t border-[#E5E5E5]" data-testid="pdp-faq">
        <h2 className="font-serif text-3xl md:text-4xl font-light tracking-tight mb-10">Frequently Asked</h2>
        <Accordion type="single" collapsible className="max-w-3xl">
          {FAQS.map((f, i) => (
            <AccordionItem key={i} value={`faq-${i}`}>
              <AccordionTrigger className="text-left font-serif text-lg">{f.q}</AccordionTrigger>
              <AccordionContent className="text-sm text-[#4a4a4a] leading-relaxed">{f.a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </section>

      {/* Related */}
      {related.length > 0 && (
        <section className="container-lira py-16 md:py-24 border-t border-[#E5E5E5]" data-testid="related-products">
          <h2 className="font-serif text-3xl md:text-4xl font-light tracking-tight mb-10">You May Also Like</h2>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-12">
            {related.map((p) => <ProductCard key={p.id} product={p} />)}
          </div>
        </section>
      )}

      {/* Recently viewed */}
      {recent.length > 0 && (
        <section className="container-lira py-16 md:py-24 border-t border-[#E5E5E5]" data-testid="recently-viewed">
          <h2 className="font-serif text-3xl md:text-4xl font-light tracking-tight mb-10">Recently Viewed</h2>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-12">
            {recent.map((p) => <ProductCard key={p.id} product={p} />)}
          </div>
        </section>
      )}

      {/* Sticky add to cart */}
      {showSticky && (
        <div className="fixed bottom-0 md:bottom-0 left-0 right-0 z-30 bg-white/95 backdrop-blur-xl border-t border-[#E5E5E5] mb-16 md:mb-0" data-testid="sticky-add-to-cart">
          <div className="container-lira py-4 flex items-center justify-between gap-4">
            <div className="flex items-center gap-4 min-w-0">
              <img src={product.images[0]} alt={product.name} className="h-12 w-10 object-cover rounded-sm hidden sm:block" />
              <div className="min-w-0">
                <p className="font-serif text-lg truncate">{product.name}</p>
                <p className="text-sm text-[#737373]">{money(product.price)}</p>
              </div>
            </div>
            <button onClick={() => addItem(product, color, qty, size)} className="btn-primary shrink-0" data-testid="sticky-add-button">Add to Bag</button>
          </div>
        </div>
      )}
    </div>
  );
}
